import type { ReactNode, Ref } from 'react';
import styles from './ModelListItem.module.css';

/**
 * Figma: `_model list item` (2055:21375) — one row of the `Model selector`'s
 * menu: an icon, the mode's name and a one-line description under it.
 *
 * `State=Default|Hover/active|State3`. `State3` is the file's unnamed third
 * variant, and both instances inside `Model selector / Open=True` use it for
 * the **selected** row (Fast is `State3`, Expert is `Default`), so that is what
 * it is here — real state carried by `aria-selected`, not a forced one.
 * Flagged in plan.md §7 for a proper name.
 *
 * The row's geometry follows the two instances in the open menu (236x36, the
 * description in `Text xs/Regular`) rather than the set's own variants (240x38,
 * both lines `Text sm`): the instances are what the composer draws, and the
 * set's placeholders were never restyled after the copy went in. Also flagged.
 *
 * Carries Figma's `_` internal marker, so it is not exported from the package
 * index — `ModelSelector` is the public surface.
 */
export type ModelListItemState = 'default' | 'hover';

export type ModelListItemProps = {
  /** Figma's upper `Text` — the mode's name. */
  label: string;
  /** Figma's lower `Text` — the mode's one-line description. */
  description: string;
  /** Figma `🔀 Icon swap`. */
  icon?: ReactNode;
  /** Figma `State=State3`. */
  selected?: boolean;
  onSelect?: () => void;
  /** Force hover for documentation and visual regression only. */
  state?: ModelListItemState;
  tabIndex?: number;
  id?: string;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
};

export function ModelListItem({
  label,
  description,
  icon,
  selected = false,
  onSelect,
  state,
  tabIndex,
  id,
  className,
  ref,
}: ModelListItemProps) {
  const classes = [
    styles.row,
    state === 'hover' ? styles.hover : null,
    // Live hover only when no state is forced, so `:hover` reacts in the app and
    // stays inert in a states story.
    state === undefined ? styles.interactive : null,
    selected ? styles.selected : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    /*
     * `role="option"` inside the selector's `listbox`: picking a mode is a
     * single-select choice, which is what a listbox announces, where a `menu`
     * would promise commands. Not in Figma.
     */
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={classes}
      onClick={onSelect}
      id={id}
      tabIndex={tabIndex}
      ref={ref}
    >
      {/* Figma `Content` — the padded, rounded surface that takes the state colour. */}
      <span className={styles.content}>
        {/* Figma `Icon and text`. */}
        {icon !== undefined && (
          <span className={styles.iconSlot} aria-hidden>
            {icon}
          </span>
        )}
        {/* Figma `Text wrapper`. */}
        <span className={styles.text}>
          <span className={styles.label}>{label}</span>
          <span className={styles.description}>{description}</span>
        </span>
      </span>
    </button>
  );
}
